import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AUDIT_DATE_PRESET_ORDER, type AuditFilterOption } from './Filters';
import {
    ACCOUNT_ACTION_OPTIONS,
    ADMIN_ACTION_OPTIONS,
    CREDENTIAL_MODE_OPTIONS,
    SUCCESS_OPTIONS,
    TARGET_TYPE_OPTIONS,
    type AuditOption,
} from './actionLabels';
import type { AuditFacetOptions } from './auditFilterTypes';

/**
 * EAA-1821: everything the Nhật ký filter surfaces need translating — the facet
 * option lists, the date vocabulary and the bar/chip copy — derived once per
 * source.
 *
 * It is a hook rather than inline in `AuditPanelShell` for two reasons. The
 * shell was past the repo's split threshold and this is the half that is pure
 * derivation, no rendering. And the memo identities matter: the bar's chips,
 * the table header's funnels and `AuditTable` itself are all `React.memo`'d, so
 * these objects must be built ONCE per language change and not per keystroke in
 * the search box.
 */

export interface AuditBarLabels {
    filters: string;
    clear: string;
    showing: string;
}

export interface AuditChipLabels {
    action: string;
    result: string;
    mode: string;
    targetType: string;
    time: string;
    all: string;
    clearSelection: string;
}

export interface AuditFacets {
    /** Shared by the bar's chips and the table header — see `AuditFacetOptions`. */
    options: AuditFacetOptions;
    barLabels: AuditBarLabels;
    chipLabels: AuditChipLabels;
    searchPlaceholder: string;
    /** Accessible name of the search box's icon-only ✕. */
    searchClearLabel: string;
}

export function useAuditFacets(isAccount: boolean): AuditFacets {
    const { t } = useTranslation();

    const translate = useCallback(
        (options: AuditOption[]): AuditFilterOption[] =>
            options.map((option) => ({ value: option.value, label: t(option.labelKey) })),
        [t]
    );

    const actionOptions = useMemo(
        () => translate(isAccount ? ACCOUNT_ACTION_OPTIONS : ADMIN_ACTION_OPTIONS),
        [translate, isAccount]
    );
    const successOptions = useMemo(() => translate(SUCCESS_OPTIONS), [translate]);
    const credentialModeOptions = useMemo(
        () => (isAccount ? translate(CREDENTIAL_MODE_OPTIONS) : []),
        [translate, isAccount]
    );
    const targetTypeOptions = useMemo(
        () => (isAccount ? [] : translate(TARGET_TYPE_OPTIONS)),
        [translate, isAccount]
    );

    const dateLabels = useMemo(
        () => ({
            presets: Object.fromEntries(
                AUDIT_DATE_PRESET_ORDER.map((preset) => [
                    preset,
                    t(`console.audit.datePresets.${preset}`),
                ])
            ),
            from: t('console.audit.filters.dateFrom', { defaultValue: 'Từ' }),
            to: t('console.audit.filters.dateTo', { defaultValue: 'Đến' }),
        }),
        [t]
    );

    // EAA-1821: ONE facet object for both surfaces — the bar's chips and
    // `AuditTable`'s column headers offer the same facets, so building a second
    // set is how the two drift. `dateLabels` rides along for the same reason:
    // the header's date funnel took no translated copy at all and fell back to
    // the hardcoded Vietnamese presets while the bar's chip spoke English.
    const options = useMemo<AuditFacetOptions>(
        () => ({
            action: actionOptions,
            credentialMode: credentialModeOptions,
            targetType: targetTypeOptions,
            success: successOptions,
            dateLabels,
        }),
        [actionOptions, credentialModeOptions, targetTypeOptions, successOptions, dateLabels]
    );

    const barLabels = useMemo(
        () => ({
            filters: t('console.audit.filters.count', { defaultValue: 'Bộ lọc' }),
            clear: t('console.audit.filters.clear', { defaultValue: 'Xoá bộ lọc' }),
            showing: t('console.audit.filters.showing', { defaultValue: 'Hiển thị' }),
        }),
        [t]
    );

    const chipLabels = useMemo(
        () => ({
            action: t('console.audit.filters.action', { defaultValue: 'Hành động' }),
            result: t('console.audit.filters.result', { defaultValue: 'Kết quả' }),
            mode: t('console.audit.filters.mode', { defaultValue: 'Chế độ' }),
            targetType: t('console.audit.filters.targetType', { defaultValue: 'Đối tượng' }),
            time: t('console.audit.filters.time', { defaultValue: 'Thời gian' }),
            all: t('console.audit.filters.all', { defaultValue: 'Tất cả' }),
            clearSelection: t('console.audit.filters.clearSelection', {
                defaultValue: 'Bỏ chọn tất cả',
            }),
        }),
        [t]
    );

    const searchPlaceholder = t('console.audit.filters.searchPlaceholder', {
        defaultValue: 'Tìm theo người thao tác, đối tượng, mã hành động…',
    });
    // EAA-1821: the ✕ inside the search box is icon-only, so it needs a name of
    // its own — it used to fall back to the placeholder, which describes the
    // FIELD ("Tìm theo người thao tác, đối tượng…") and not what the button does.
    const searchClearLabel = t('console.audit.filters.searchClear', {
        defaultValue: 'Xoá tìm kiếm',
    });

    return { options, barLabels, chipLabels, searchPlaceholder, searchClearLabel };
}
